import { useState } from 'react';
import { Check, Copy, KeyRound } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { TempCredentials } from '../types';

interface TempPasswordDialogProps {
  credentials: TempCredentials | null;
  onClose: () => void;
}

/** The temporary password is shown only here, once. Copying it is the only way to hand it over. */
export function TempPasswordDialog({ credentials, onClose }: TempPasswordDialogProps) {
  const [copied, setCopied] = useState<'password' | 'all' | null>(null);

  const copy = async (what: 'password' | 'all') => {
    if (!credentials) return;
    const text = what === 'password'
      ? credentials.password
      : `Usuario: ${credentials.username}\nContraseña temporal: ${credentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* the user can still select the text */
    }
  };

  return (
    <Dialog open={!!credentials} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="size-5" /> Contraseña temporal</DialogTitle>
          <DialogDescription>Entrega estos datos a {credentials?.name}. La contraseña se muestra solo ahora.</DialogDescription>
        </DialogHeader>
        {credentials && (
          <div className="space-y-3">
            <dl className="space-y-2 rounded-md border bg-muted/40 p-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">Usuario</dt>
                <dd className="font-mono text-base font-semibold" data-testid="temp-username">{credentials.username}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">Contraseña</dt>
                <dd className="select-all font-mono text-base font-semibold" data-testid="temp-password">{credentials.password}</dd>
              </div>
            </dl>
            <Alert>
              <AlertDescription>
                Al entrar por primera vez, el sistema le pedirá crear una contraseña nueva.
              </AlertDescription>
            </Alert>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => void copy('password')}>
            {copied === 'password' ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
            Copiar contraseña
          </Button>
          <Button onClick={() => void copy('all')}>
            {copied === 'all' ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
            Copiar usuario y contraseña
          </Button>
        </DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cerrar</Button>
      </DialogContent>
    </Dialog>
  );
}
